import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from "react";
import clsx from "clsx";
import Dialog from "@material-ui/core/Dialog";
import TextField from "@material-ui/core/TextField";
import { makeStyles } from "@material-ui/core/styles";

// Diálogo de confirmação único do produto (substitui os estilizados à mão).
//   const { confirm, prompt } = useConfirm();
//   // booleano (retrocompatível):
//   if (await confirm({ title, message, confirmText, danger })) { ... }
//   // múltiplas ações (variante "unpublished" etc.):
//   const r = await confirm({ title, body, actions: [
//     { label: "Continuar editando", kind: "ghost", value: "edit" },
//     { label: "Sair como rascunho", kind: "sec",   value: "draft" },
//     { label: "Publicar e sair",    kind: "primary", value: "publish" },
//   ], dismissOnScrim: true });
//   const name = await prompt({ title, label, defaultValue }); // string | null
// Regra de tipografia (a que vivia quebrada): TÍTULO forte (18/600), CORPO leve
// (14/400, NUNCA bold). Hierarquia de botões: ghost → sec → primary/danger.
const ConfirmContext = createContext({
  confirm: async () => false,
  prompt: async () => null,
});

export const useConfirm = () => useContext(ConfirmContext);

const useStyles = makeStyles(theme => ({
  backdrop: { background: "rgba(16,20,26,.55)", backdropFilter: "blur(1px)" },
  paper: {
    width: 440,
    maxWidth: "92vw",
    margin: 0,
    borderRadius: 14,
    border: `1px solid ${theme.app.border}`,
    boxShadow: "0 24px 60px rgba(16,20,26,.35)",
    padding: 24,
  },
  title: { fontSize: 18, fontWeight: 600, color: theme.app.ink, margin: "0 0 8px", lineHeight: 1.3 },
  // corpo: leve, NUNCA bold, e sempre menor que o título
  body: { fontSize: 14, fontWeight: 400, lineHeight: 1.55, color: theme.app.textSecondary, margin: 0, whiteSpace: "pre-wrap" },
  field: { marginTop: 16 },
  row: { display: "flex", justifyContent: "flex-end", gap: 10, flexWrap: "wrap", marginTop: 22 },
  btn: {
    font: "inherit",
    fontSize: 13.5,
    fontWeight: 500,
    padding: "9px 16px",
    borderRadius: 8,
    cursor: "pointer",
    border: "1px solid transparent",
    transition: "background .12s, border-color .12s",
  },
  ghost: { background: "#fff", color: theme.app.textSecondary, borderColor: theme.app.border, "&:hover": { background: "#F4F5F7" } },
  sec: { background: "#fff", color: theme.app.ink, borderColor: "#C7CCD2", "&:hover": { background: "#FAFBFC" } },
  primary: { background: theme.app.dark, color: "#fff", "&:hover": { background: "#1f262c" } },
  danger: { background: theme.app.danger, color: "#fff", "&:hover": { background: "#a93b32" } },
  btnOff: { opacity: 0.5, cursor: "default", "&:hover": { background: theme.app.dark } },
}));

export const ConfirmProvider = ({ children }) => {
  const classes = useStyles();
  const [state, setState] = useState(null);
  const [value, setValue] = useState("");
  const resolveRef = useRef(null);
  const safeRef = useRef(null);

  const open = useCallback((opts, isPrompt) => {
    setValue(opts.defaultValue || "");
    setState({ isPrompt, ...opts });
    return new Promise(resolve => {
      resolveRef.current = resolve;
    });
  }, []);

  const confirm = useCallback(opts => open(opts || {}, false), [open]);
  const prompt = useCallback(opts => open(opts || {}, true), [open]);

  const close = result => {
    if (resolveRef.current) resolveRef.current(result);
    resolveRef.current = null;
    setState(null);
  };

  const s = state || {};
  const isDestructive = s.variant === "destructive" || (!s.variant && !!s.danger);
  const hasActions = Array.isArray(s.actions) && s.actions.length > 0;

  // monta a lista de botões + qual o valor de "cancelar" (ESC / scrim)
  let buttons;
  let cancelValue;
  if (hasActions) {
    buttons = s.actions.map((a, i) => ({
      label: a.label,
      kind: a.kind || (i === s.actions.length - 1 ? "primary" : "ghost"),
      value: a.value,
    }));
    cancelValue = s.actions[0] ? s.actions[0].value : null; // ESC/scrim = ação segura (1ª)
  } else if (s.isPrompt) {
    buttons = [
      { label: s.cancelText || "Cancelar", kind: "ghost", value: "__CANCEL__" },
      { label: s.confirmText || "Salvar", kind: "primary", value: "__OK__", primary: true, disabled: !value.trim() },
    ];
    cancelValue = null;
  } else {
    buttons = [
      { label: s.cancelText || "Cancelar", kind: "ghost", value: false },
      { label: s.confirmText || "Confirmar", kind: isDestructive ? "danger" : "primary", value: true, primary: true },
    ];
    cancelValue = false;
  }

  const onButton = b => {
    if (b.disabled) return;
    if (b.value === "__OK__") return close(value);
    if (b.value === "__CANCEL__") return close(null);
    return close(b.value);
  };

  // ação primária (para Enter) — nunca em destrutivo
  const primaryBtn = buttons.find(b => b.primary);
  const dismissOnScrim = s.dismissOnScrim != null ? s.dismissOnScrim : !isDestructive;

  const handleClose = (e, reason) => {
    if (reason === "backdropClick" && !dismissOnScrim) return;
    close(cancelValue);
  };

  // foco na ação segura ao abrir (no prompt, o foco vai pro input)
  useEffect(() => {
    if (state && !state.isPrompt && safeRef.current) {
      const t = setTimeout(() => {
        try {
          safeRef.current.focus();
        } catch (e) {
          /* ignore */
        }
      }, 30);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [state]);

  const onKeyDown = e => {
    if (e.key === "Enter" && !s.isPrompt && primaryBtn && !isDestructive) {
      e.preventDefault();
      onButton(primaryBtn);
    }
  };

  return (
    <ConfirmContext.Provider value={{ confirm, prompt }}>
      {children}
      <Dialog
        open={!!state}
        onClose={handleClose}
        maxWidth={false}
        BackdropProps={{ className: classes.backdrop }}
        PaperProps={{
          className: classes.paper,
          role: "alertdialog",
          "aria-labelledby": "nz-dlg-title",
          "aria-describedby": "nz-dlg-body",
          onKeyDown,
        }}
      >
        {s.title && (
          <h2 id="nz-dlg-title" className={classes.title}>
            {s.title}
          </h2>
        )}
        {(s.body || s.message) && (
          <p id="nz-dlg-body" className={classes.body}>
            {s.body || s.message}
          </p>
        )}
        {s.isPrompt && (
          <TextField
            className={classes.field}
            autoFocus
            fullWidth
            variant="outlined"
            size="small"
            label={s.label || ""}
            value={value}
            onChange={e => setValue(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter") {
                // evita o Enter borbulhar/disparar ação default que reabria o modal
                e.preventDefault();
                e.stopPropagation();
                if (value.trim()) close(value);
              }
            }}
          />
        )}
        <div className={classes.row}>
          {buttons.map((b, i) => (
            <button
              key={i}
              type="button"
              ref={i === 0 ? safeRef : null}
              className={clsx(classes.btn, classes[b.kind], b.disabled && classes.btnOff)}
              onClick={() => onButton(b)}
              disabled={b.disabled}
            >
              {b.label}
            </button>
          ))}
        </div>
      </Dialog>
    </ConfirmContext.Provider>
  );
};

export default ConfirmProvider;
